import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import config from "../../config";
import { encryptData } from "../../utils/cryptoUtils";

const operatorSets = {
  numeric: { EQ: 'Equals', NE: 'Not Equals', GT: 'Greater Than', GTE: 'Greater Than or Equal', LT: 'Less Than', LTE: 'Less Than or Equal', BETWEEN: 'Between' },
  text: { TEXT_EQ: 'Equals', TEXT_NE: 'Not Equals', CONTAINS: 'Contains', NOT_CONTAINS: 'Does Not Contain', STARTS_WITH: 'Starts With', ENDS_WITH: 'Ends With', IN_LIST: 'In List', NOT_IN_LIST: 'Not In List' },
  date: { EQ: 'Equals', NE: 'Not Equals', GT: 'After', GTE: 'On/After', LT: 'Before', LTE: 'On/Before', BETWEEN: 'Between' },
  boolean: { EQ: 'Equals' }
};

const getOptionsFor = (type) => {
  return type === 'TEXT' ? operatorSets.text : (type === 'DATE' ? operatorSets.date : (type === 'BOOLEAN' ? operatorSets.boolean : operatorSets.numeric));
};

const getDefaultCondition = (dataType) => {
  const options = getOptionsFor(dataType);
  const firstOperator = Object.keys(options)[0] || 'EQ';
  return {
    operator: dataType === 'DECIMAL' ? 'BETWEEN' : firstOperator,
    min_value: '',
    max_value: '',
    min_inclusive: 1,
    max_inclusive: 1,
    case_sensitive: 0,
    score: ''
  };
};

const getDefaultParameter = () => ({
  id: 0,
  parameter_code: '',
    parameter_name: '',
  parameter_linking_name: '',
  data_type: 'DECIMAL',
  mandatory: 1,
  conditions: [getDefaultCondition('DECIMAL')]
});

const AddBusinessRulesEngine = () => {
  const [rule, setRule] = useState({
    id: 0,
    rule_code: '',
    rule_name: '',
    description: '',
    aggregation_type: 'AVERAGE',
    status: 'DRAFT',
    effective_from: '',
    effective_to: ''
  });
  const [parameters, setParameters] = useState([getDefaultParameter()]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
    const [tableLinkings, setTableLinkings] = useState([]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');

  useEffect(() => {
    if (id) {
      fetchRuleData(id);
    }
  }, [id]);

    useEffect(() => {
    fetchTableLinkings();
  }, []);

  const fetchTableLinkings = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}get_table_linkings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: encryptData({}),
      });
      const data = await res.json();
      if (data.status === 200 && Array.isArray(data.data)) {
        setTableLinkings(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch table linkings', err);
    }
  };

  const fetchRuleData = async (ruleId) => {
    try {
      setLoading(true);
      const res = await fetch(`${config.API_BASE_URL}get_business_rule_by_id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: encryptData({ id: ruleId }),
      });
      const data = await res.json();
      if (data.status === 200 && data.data) {
        setRule({
          id: data.data.id || 0,
          rule_code: data.data.rule_code || '',
          rule_name: data.data.rule_name || '',
          description: data.data.description || '',
          aggregation_type: data.data.aggregation_type || 'AVERAGE',
          status: data.data.status || 'DRAFT',
          effective_from: data.data.effective_from || '',
          effective_to: data.data.effective_to || ''
        });
        
        if (data.data.parameters && data.data.parameters.length > 0) {
          // Map data carefully to match state shape
          const loadedParams = data.data.parameters.map(p => ({
            id: p.id,
            parameter_code: p.parameter_code,
            parameter_name: p.parameter_name,
            parameter_linking_name: p.parameter_linking_name || '',
            data_type: p.data_type,
            mandatory: parseInt(p.mandatory, 10) || 0,
            conditions: (p.conditions && p.conditions.length > 0) ? p.conditions.map(c => ({
              id: c.id,
              operator: c.operator,
              min_value: c.text_value !== null ? c.text_value : (c.min_value !== null ? c.min_value : ''),
              max_value: c.text_value_2 !== null ? c.text_value_2 : (c.max_value !== null ? c.max_value : ''),
              min_inclusive: parseInt(c.min_inclusive, 10) || 0,
              max_inclusive: parseInt(c.max_inclusive, 10) || 0,
              case_sensitive: parseInt(c.case_sensitive, 10) || 0,
              score: c.score
            })) : [getDefaultCondition(p.data_type)]
          }));
          setParameters(loadedParams);
        }
      } else {
        toast.error(data.message || 'Failed to fetch rule data');
        navigate('/dashboard/business-rules-engine');
      }
    } catch (err) {
      toast.error('Something went wrong while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  const handleRuleChange = (e) => {
  const { name, value } = e.target;
  setRule(prev => {
    const updated = { ...prev, [name]: value };
    if (name === 'rule_name') {
      updated.rule_code = toCode(value);
    }
    return updated;
  });
};

  const handleParameterChange = (pi, field, value) => {
  const newParams = [...parameters];
  newParams[pi] = { ...newParams[pi], [field]: value };

  // Parameter name se parameter code auto-generate
  if (field === 'parameter_name') {
    newParams[pi].parameter_code = toCode(value);
  }

  // Auto-reset conditions if data_type changes
  if (field === 'data_type') {
    newParams[pi].conditions = [getDefaultCondition(value)];
  }

  setParameters(newParams);
};

  const handleConditionChange = (pi, ci, field, value) => {
    const newParams = [...parameters];
    newParams[pi].conditions[ci][field] = value;
    setParameters(newParams);
  };

  const addParameter = () => {
    setParameters([...parameters, getDefaultParameter()]);
  };

  const removeParameter = (pi) => {
    if (parameters.length > 1) {
      const newParams = [...parameters];
      newParams.splice(pi, 1);
      setParameters(newParams);
    }
  };

  const addCondition = (pi) => {
    const newParams = [...parameters];
    newParams[pi].conditions.push(getDefaultCondition(newParams[pi].data_type));
    setParameters(newParams);
  };

  const removeCondition = (pi, ci) => {
    if (parameters[pi].conditions.length > 1) {
      const newParams = [...parameters];
      newParams[pi].conditions.splice(ci, 1);
      setParameters(newParams);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for scores
    let hasError = false;
    parameters.forEach(p => {
      p.conditions.forEach(c => {
        const s = parseFloat(c.score);
        if (Number.isNaN(s) || s < 0 || s > 100) {
          hasError = true;
        }
      });
    });
    
    if (hasError) {
      toast.error('Every score must be between 0 and 100.');
      return;
    }

    setSubmitting(true);
    try {
      // Expand comma-separated text lists into separate condition rows.
      // Conditions in one parameter are OR alternatives in the evaluator.
      const savedParameters = parameters.map(p => ({
        ...p,
        conditions: p.conditions.flatMap(c => {
          const usesTextList = p.data_type === 'TEXT' &&
            ['CONTAINS', 'IN_LIST'].includes(c.operator) &&
            String(c.max_value || '').trim() !== '';

          if (!usesTextList) return [c];

          const values = [c.min_value, c.max_value]
            .flatMap(value => String(value || '').split(','))
            .map(value => value.trim())
            .filter(Boolean);

          return values.map(value => ({
            ...c,
            operator: c.operator === 'IN_LIST' ? 'TEXT_EQ' : c.operator,
            min_value: value,
            max_value: ''
          }));
        })
      }));

      const payload = {
        ...rule,
        parameters: savedParameters
      };
      
      const res = await fetch(`${config.API_BASE_URL}save_business_rule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: encryptData(payload),
      });
      const data = await res.json();
      
      if (data.status === 200) {
        toast.success("Rule saved successfully!");
        navigate('/dashboard/business-rules-engine');
      } else {
        toast.error(data.message || "Failed to save rule");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toCode = (str) =>
  String(str || '')
    .trim()
    .replace(/\s+/g, '_');

  if (loading) return <div className="container mt-4">Loading...</div>;

  return (
    <div className="container mt-4">
      <form onSubmit={handleSubmit} id="ruleForm">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3>{id ? 'Edit Business Rule' : 'Create Business Rule'}</h3>
            <div className="text-muted">Configure numeric ranges or natural-language-style text conditions.</div>
          </div>
          <div>
            <Link to="/dashboard/business-rules-engine" className="btn btn-outline-secondary me-2">Cancel</Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Rule'}
            </button>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">1. Rule Details</h5>
          </div>
          <div className="card-body row g-3">
            <div className="col-md-3">
              <label className="form-label">Rule Code *</label>
              <input required readOnly name="rule_code" className="form-control bg-light" value={rule.rule_code} />
            </div>
            <div className="col-md-5">
              <label className="form-label">Rule Name *</label>
              <input required name="rule_name" className="form-control" value={rule.rule_name} onChange={handleRuleChange} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Aggregation</label>
              <select name="aggregation_type" className="form-select" value={rule.aggregation_type} onChange={handleRuleChange}>
                {['SUM', 'AVERAGE', 'MAX', 'MIN'].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <input readOnly className="form-control bg-light" value={rule.status} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Effective From</label>
              <input type="date" name="effective_from" className="form-control" value={rule.effective_from || ''} onChange={handleRuleChange} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Effective To</label>
              <input type="date" name="effective_to" className="form-control" value={rule.effective_to || ''} onChange={handleRuleChange} />
            </div>
            <div className="col-md-12">
              <label className="form-label">Description</label>
              <textarea name="description" className="form-control" rows="2" value={rule.description || ''} onChange={handleRuleChange}/>
            </div>
          </div>
        </div>

        <div id="parameters">
          {parameters.map((p, pi) => {
            const isText = p.data_type === 'TEXT';
            const isDate = p.data_type === 'DATE';
            const isBool = p.data_type === 'BOOLEAN';
            
            return (
              <div key={pi} className={`card mb-4 ${isText ? 'border-primary' : ''}`}>
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">2. Parameter {pi + 1}</h5>
                  {parameters.length > 1 && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeParameter(pi)}>Remove</button>
                  )}
                </div>
                <div className="card-body">
                  {/* <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label className="form-label">Parameter Code *</label>
                      <input required readOnly className="form-control bg-light" value={p.parameter_code} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Parameter Name *</label>
                      <input required className="form-control" value={p.parameter_name} onChange={e => handleParameterChange(pi, 'parameter_name', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Data Type</label>
                      <select className="form-select" value={p.data_type} onChange={e => handleParameterChange(pi, 'data_type', e.target.value)}>
                        {['INTEGER', 'DECIMAL', 'TEXT', 'DATE', 'BOOLEAN'].map(dt => <option key={dt} value={dt}>{dt}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={!!p.mandatory} onChange={e => handleParameterChange(pi, 'mandatory', e.target.checked ? 1 : 0)} />
                        <label className="form-check-label" title="Checked parameters are required and combine with AND. Unchecked parameters are OR alternatives.">Mandatory (AND)</label>
                      </div>
                    </div>
                  </div> */}

                    <div className="row g-3 mb-4">
                    <div className="col-md-2">
                      <label className="form-label">Parameter Code *</label>
                      <input required readOnly className="form-control bg-light" value={p.parameter_code} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Parameter Name *</label>
                      <input required className="form-control" value={p.parameter_name} onChange={e => handleParameterChange(pi, 'parameter_name', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Table Linking</label>
                      <select className="form-select" value={p.parameter_linking_name || ''} onChange={e => handleParameterChange(pi, 'parameter_linking_name', e.target.value)}>
                        <option value="">-- Select --</option>
                        {tableLinkings.map(t => (
                          <option key={t.id} value={String(t.id)}>{t.section_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Data Type</label>
                      <select className="form-select" value={p.data_type} onChange={e => handleParameterChange(pi, 'data_type', e.target.value)}>
                        {['INTEGER', 'DECIMAL', 'TEXT', 'DATE', 'BOOLEAN'].map(dt => <option key={dt} value={dt}>{dt}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={!!p.mandatory} onChange={e => handleParameterChange(pi, 'mandatory', e.target.checked ? 1 : 0)} />
                        <label className="form-check-label" title="Checked parameters are required and combine with AND. Unchecked parameters are OR alternatives.">Mandatory (AND)</label>
                      </div>
                    </div>
                  </div>

                  <div className="conditions">
                    {p.conditions.map((c, ci) => (
                      <div key={ci} className="row g-2 align-items-end mb-3 border-bottom pb-3">
                        <div className="col-md-2">
                          <label className="form-label">Operator</label>
                          <select className="form-select" value={c.operator} onChange={e => handleConditionChange(pi, ci, 'operator', e.target.value)}>
                            {Object.entries(getOptionsFor(p.data_type)).map(([val, label]) => (
                              <option key={val} value={val}>{label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">
                            {isText ? 'Text / Value' : (isDate ? 'Date / From' : 'Value / Min')}
                          </label>
                          <input 
                            type={isDate ? 'date' : (isText || isBool ? 'text' : 'number')}
                            className="form-control" 
                            placeholder={isBool ? 'true / false' : (isText ? 'e.g. Delhi' : '')}
                            value={c.min_value} 
                            onChange={e => handleConditionChange(pi, ci, 'min_value', e.target.value)} 
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">
                            {isText ? 'Second Value / List' : (isDate ? 'To' : 'Max')}
                          </label>
                          <input 
                            type={isDate ? 'date' : (isText ? 'text' : 'number')}
                            className="form-control"
                            placeholder={isText ? 'Optional second value' : ''}
                            disabled={isBool}
                            value={c.max_value} 
                            onChange={e => handleConditionChange(pi, ci, 'max_value', e.target.value)} 
                          />
                        </div>
                        
                        {!isText && !isBool && (
                          <>
                            <div className="col-md-1">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" checked={!!c.min_inclusive} onChange={e => handleConditionChange(pi, ci, 'min_inclusive', e.target.checked ? 1 : 0)} />
                                <label className="form-check-label small">Min incl.</label>
                              </div>
                            </div>
                            <div className="col-md-1">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" checked={!!c.max_inclusive} onChange={e => handleConditionChange(pi, ci, 'max_inclusive', e.target.checked ? 1 : 0)} />
                                <label className="form-check-label small">Max incl.</label>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {isText && (
                          <div className="col-md-1">
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" checked={!!c.case_sensitive} onChange={e => handleConditionChange(pi, ci, 'case_sensitive', e.target.checked ? 1 : 0)} />
                              <label className="form-check-label small">Case</label>
                            </div>
                          </div>
                        )}

                        <div className="col-md-2">
                          <label className="form-label">Score *</label>
                          <input required type="number" min="0" max="100" step=".01" className="form-control" value={c.score} onChange={e => handleConditionChange(pi, ci, 'score', e.target.value)} />
                        </div>
                        <div className="col-md-1">
                          {p.conditions.length > 1 && (
                            <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeCondition(pi, ci)}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addCondition(pi)}>+ Add Condition</button>
                  {isText && (
                    <div className="form-text mt-2 text-primary">
                      For TEXT, examples: "Address contains Delhi", "City equals Delhi", "Email ends with @gmail.com", or "Customer type is in Retail, Corporate".
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mb-4">
          <button type="button" className="btn btn-outline-primary" onClick={addParameter}>+ Add Parameter</button>
        </div>
        
        <div className="alert alert-info">
          <strong>Score rule:</strong> every condition score must be between 0 and 100. Text rules are case-insensitive by default.
        </div>
      </form>
    </div>
  );
};

export default AddBusinessRulesEngine;
